<template>
	<input
		v-bind="$attrs"
		:id="context.id"
		v-model="enteredDate"
		:class="context.classes.input"
		:name="context.node.name"
		:disabled="context.disabled as boolean"
		@blur="onBlur"
	/>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";
import type { FormKitFrameworkContext } from "@formkit/core";
import { DateTime } from "luxon";
import {
	fuzzyDateParse,
	type FuzzyDateParseOptions,
} from "@loanlink-nl/migration";

const props = defineProps<{
	context: FormKitFrameworkContext;
}>();

const enteredDate = ref<string>();
const hdnDate = computed(() => props.context.value);
const parseOptions = computed<FuzzyDateParseOptions>(() => ({
	twoDigitYearCentury: props.context.node.props.twoDigitYearCentury as
		| FuzzyDateParseOptions["twoDigitYearCentury"]
		| undefined,
}));

function enteredDateHook<E, V>(
	entered: Ref<E>,
	val: Ref<V>,
	opts: {
		enteredToVal: (entered: E) => V;
		valToEntered: (val: V) => E;
		isEqual: (entered: E, val: V) => boolean;
		changeVal: (val: V) => Promise<void>;
		changeEntered: (entered: E) => void;
	},
) {
	watch(entered, async (entered) => {
		if (!opts.isEqual(entered, val.value)) {
			await opts.changeVal(opts.enteredToVal(entered));
		}
	});

	watch(
		val,
		(val) => {
			if (!opts.isEqual(entered.value, val)) {
				opts.changeEntered(opts.valToEntered(val));
			}
		},
		{ immediate: true, deep: true },
	);
}

function enteredToVal(date?: string) {
	if (!date) {
		return;
	}

	return fuzzyDateParse(date, parseOptions.value).toISODate() ?? undefined;
}

function valToEntered(date?: string) {
	if (!date) {
		return;
	}

	const parsedDate = DateTime.fromISO(date);
	if (!parsedDate.isValid) {
		return date;
	}

	return parsedDate.toFormat("d-M-yyyy");
}

function isEqual(dateEntered?: string, dateValue?: string) {
	const parsedDate = enteredToVal(dateEntered);
	return parsedDate === dateValue;
}

enteredDateHook(enteredDate, hdnDate, {
	enteredToVal,
	valToEntered,
	isEqual,
	changeVal: async (val) => {
		await props.context.node.input(val);
	},
	changeEntered: (entered) => (enteredDate.value = entered),
});

function onBlur(e: FocusEvent) {
	const newDate = valToEntered(enteredToVal(enteredDate.value));
	if (newDate !== undefined) {
		enteredDate.value = newDate;
	}

	props.context.handlers.blur(e);
}
</script>
